import ApiService from "@/services/Api"

export type MeetingAnalysisStatus = "processing" | "success" | "failed"

export type MeetingScoreLabel = "Alto" | "Medio" | "Baixo"

export type MeetingProfile = "aggressive" | "balanced" | "passive" | "technical"

export type MeetingObjection = "price" | "timing" | "authority" | "need" | "trust"

export type MeetingTopicScores = {
	discovery: number
	pain: number
	value: number
	conduction: number
	closing: number
}

export type MeetingPhase = "opening" | "discovery" | "pain-exploration" | "value-demonstration" | "objection-handling" | "closing"

export type MeetingParticipants = {
	closerName: string | null
	leadName: string | null
	leadCompany: string | null
}

export type MeetingCloserProfile = {
	type: MeetingProfile
	summary: string
}

export type MeetingSpeechSplit = {
	closer: number
	client: number
}

export type MeetingMainObjection = {
	category: MeetingObjection
	text: string
	excerpt: string
}

export type MeetingImprovementOpportunity = {
	topic: keyof MeetingTopicScores
	excerpt: string
	suggestion: string
}

export type MeetingHitOrMiss = {
	phase: MeetingPhase
	excerpt: string
	reason: string
}

export type MeetingAnalysisOutput = {
	participants: MeetingParticipants
	meetingDate: string | null
	generalScore: number
	topicScores: MeetingTopicScores
	closerProfile: MeetingCloserProfile
	speechSplit: MeetingSpeechSplit
	mainObjection: MeetingMainObjection
	conversionProbability: number
	improvementOpportunities: MeetingImprovementOpportunity[]
	mainHit: MeetingHitOrMiss
	mainMiss: MeetingHitOrMiss
	nextSteps: string[]
}

export type MeetingAnalysis = {
	id: number
	userId: number
	userName: string
	status: MeetingAnalysisStatus
	error?: Record<string, unknown>
	input: {
		transcription: string
	}
	output: MeetingAnalysisOutput | null
	createdAt: string
	updatedAt: string
}

export type CreateMeetingAnalysisInput = {
	userId: number
	transcription: string
}

export type UpdateMeetingDateInput = {
	meetingDate: string
}

export type RetrieveMeetingAnalysesFilters = {
	search?: string
	userId?: number
	profile?: MeetingProfile
	period?: string
	scoreLabel?: MeetingScoreLabel
	page?: number
	limit?: number
}

type CreateMeetingAnalysisResponse = {
	analysis: MeetingAnalysis
}

type RetrieveMeetingAnalysesResponse = {
	analyses: MeetingAnalysis[]
	count: number
}

type RetrieveMeetingAnalysisResponse = {
	analysis: MeetingAnalysis
}

class MeetingAnalyzerService {
	async create (input: CreateMeetingAnalysisInput): Promise<MeetingAnalysis> {
		const { data } = await ApiService.post<CreateMeetingAnalysisResponse>("/ai-analyses/type/closer-meeting", {
			userId: input.userId,
			input: { transcription: input.transcription }
		})

		return data.analysis
	}

	async retrieveAll (filters: RetrieveMeetingAnalysesFilters): Promise<RetrieveMeetingAnalysesResponse> {
		const { data } = await ApiService.get<RetrieveMeetingAnalysesResponse>("/ai-analyses/type/closer-meeting", {
			params: filters
		})

		return data
	}

	async retrieveById (id: number): Promise<MeetingAnalysis> {
		const { data } = await ApiService.get<RetrieveMeetingAnalysisResponse>(`/ai-analyses/${id}`)

		return data.analysis
	}

	async updateDate (id: number, input: UpdateMeetingDateInput): Promise<void> {
		await ApiService.patch(`/ai-analyses/${id}/type/meeting-analyzer/date`, input)
	}

	async delete (id: number): Promise<void> {
		await ApiService.delete(`/ai-analyses/${id}`)
	}
}

export default new MeetingAnalyzerService()
